import { Stack } from '@mui/material';
import { ErrorText, MHMTypography } from '../MHM/MHMTypography';
import { useTranslation } from '../../../contexts/TranslationContext';
import { MHMTextField } from '../MHM/MHMTextField';
import { useRecoilState } from 'recoil';
import { changeUserAtom } from '../../../models/atoms/account_atoms';
import { useState, type ChangeEvent } from 'react';
import { validateNames } from '../../../utils/account_validations';
import { PhoneNumberInput } from './PhoneNumberInput';
import { MHMButton } from '../MHM/MHMButton';
import { account_controller } from '../../../controllers/account_controller';
import { ConfirmSaveModal } from './ConfirmSaveModal';

export function AccountInfoSettings() {
  const { t } = useTranslation();
  const [changeUser, setChangeUser] = useRecoilState(changeUserAtom);
  const [nameValidationError, setNameValidationError] = useState('');
  const [phoneValidationError, setPhoneValidationError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  function openModal() {
    setIsModalOpen(true);
  }

  function closeModal() {
    setIsModalOpen(false);
  }

  function handleFirstNameChange(e: ChangeEvent<HTMLInputElement>) {
    if (changeUser === null) {
      return;
    }

    const newFirstName = e.target.value;
    setChangeUser(prev =>
      prev
        ? { ...prev, first_name: newFirstName, change_first_name: true }
        : null,
    );
    setNameValidationError(
      validateNames(newFirstName, changeUser.last_name) ?? '',
    );
  }

  function handleLastNameChange(e: ChangeEvent<HTMLInputElement>) {
    if (changeUser === null) {
      return;
    }

    const newLastName = e.target.value;
    setChangeUser(prev =>
      prev ? { ...prev, last_name: newLastName, change_last_name: true } : null,
    );
    setNameValidationError(
      validateNames(changeUser.first_name, newLastName) ?? '',
    );
  }

  function handlePhoneNumberChange(phone: string) {
    if (changeUser === null) {
      return;
    }

    setChangeUser(prev =>
      prev ? { ...prev, phone_number: phone, change_email_phone: true } : null,
    );
  }

  function saveAccountInfo() {
    if (changeUser === null || !isChangeUserValid) {
      return;
    }

    account_controller.modify_user_account({
      email: changeUser.email,
      phone_number: changeUser.phone_number,
      first_name: changeUser.first_name,
      last_name: changeUser.last_name,
    });

    closeModal();
  }

  if (!changeUser) {
    return null;
  }

  const isChangeUserValid =
    (changeUser.change_email_phone ||
      changeUser.change_first_name ||
      changeUser.change_last_name) &&
    !phoneValidationError &&
    !nameValidationError;

  console.log('isChangeUserValid', isChangeUserValid, {
    changeUser,
    phoneValidationError,
    nameValidationError,
  });

  return (
    <>
      <Stack gap={2} flex={1}>
        <MHMTypography variant="h6" color="primary">
          {t('Account Information')}
        </MHMTypography>

        <MHMTextField
          label={t('First Name')}
          value={changeUser.first_name}
          fullWidth
          size="small"
          onChange={handleFirstNameChange}
          error={Boolean(nameValidationError)}
        />

        <MHMTextField
          label={t('Last Name')}
          value={changeUser.last_name}
          fullWidth
          size="small"
          onChange={handleLastNameChange}
          error={Boolean(nameValidationError)}
          helperText={t(nameValidationError)}
        />

        <MHMTextField
          label={t('Email')}
          value={changeUser.email}
          fullWidth
          size="small"
          disabled
          aria-readonly
        />
        <PhoneNumberInput
          label={t('Phone Number')}
          value={changeUser.phone_number}
          setPhoneValidationError={setPhoneValidationError}
          onValid={handlePhoneNumberChange}
          error={Boolean(phoneValidationError)}
          helperText={
            <>
              <MHMTypography component="span">
                {t('+country code without leading zero or spaces')}
                <br />
                {t('(e.g. +447XXXXXXXXX)')}
              </MHMTypography>
              {phoneValidationError ? (
                <ErrorText>{t(phoneValidationError)}</ErrorText>
              ) : null}
            </>
          }
        />

        {isChangeUserValid && (
          <ErrorText fontSize="small">{t('Changes not yet saved.')}</ErrorText>
        )}
        <MHMButton
          disabled={!isChangeUserValid}
          onClick={openModal}
          sx={{ width: 'fit-content' }}
        >
          {t('Save')}
        </MHMButton>
      </Stack>

      <ConfirmSaveModal
        open={isModalOpen}
        onCancel={closeModal}
        onConfirm={saveAccountInfo}
        message={t('Are you sure your new account information is correct?')}
      />
    </>
  );
}
